/**
 * Pack Selector
 * Horizontal pack tier bar (True Classic style).
 * Reads discount percentage tiers from data attributes, calculates
 * euro savings and per-item prices from the current variant price.
 * Follows established IIFE + polling pattern (see multipack-quick-add.js).
 */
(function() {
  'use strict';

  var SUCCESS_DURATION = 2000;
  var ERROR_DURATION = 5000;

  /**
   * Format cents to Finnish euro string: "12,49 €"
   * Shopify prices are in the store's minor unit (cents for EUR).
   */
  function formatMoney(cents) {
    var euros = (cents / 100).toFixed(2).replace('.', ',');
    return euros + '\u20AC'; // € sign, no space — matches Shopify | money filter
  }

  function initPackSelector(container) {
    if (container.dataset.packInit === 'true') return;
    container.dataset.packInit = 'true';

    var options = container.querySelectorAll('.pack-selector__option');
    var addBtn = container.querySelector('[data-pack-add-to-cart]');
    var textEl = addBtn ? addBtn.querySelector('[data-pack-add-text]') : null;
    var loadingEl = addBtn ? addBtn.querySelector('.pack-selector__add-loading') : null;
    var successEl = addBtn ? addBtn.querySelector('.pack-selector__add-success') : null;
    var errorEl = container.querySelector('.pack-selector__error');
    var summaryEl = container.querySelector('[data-pack-summary]');

    if (!options.length || !addBtn) return;

    // Native buy-buttons are hidden via CSS :has(.pack-selector) in the stylesheet

    // Current state — default is 1 kpl (singles, no discount)
    var selectedQuantity = 1;
    var selectedPct = 0;
    // Number of items given free by a BXGY tier (0 for percentage tiers).
    // Socks run "Ota 3 maksa 2", where the saving is one unit price exactly —
    // a percentage would round wrong: 33% of 3x9,99 is 10,04, not 9,99.
    var selectedFree = 0;

    function packMath(qty, pct, free) {
      if (free > 0) {
        var paid = qty - free;
        return { total: basePrice * paid, savings: basePrice * free, unit: Math.round(basePrice * paid / qty) };
      }
      var perItem = Math.floor(basePrice * pct / 100);
      return { total: (basePrice - perItem) * qty, savings: perItem * qty, unit: basePrice - perItem };
    }
    var basePrice = parseInt(container.dataset.basePrice, 10) || 0;

    function getVariantId() {
      var form = container.closest('product-form-component, .product-form, [data-product-form]');

      if (!form) {
        // Scope the fallback to whatever product context this selector lives
        // in. Searching the whole document found the underlying product page's
        // form when the selector was inside the quick-add modal, so clicking
        // "Lisää" added three of the page's product instead of the one on
        // screen — it reported success, for the wrong item.
        var scope = container.closest('#quick-add-modal-content, .shopify-section') || document;
        form = scope.querySelector('product-form-component, .product-form, [data-product-form]');
      }

      if (!form) return null;
      var input = form.querySelector('input[name="id"]');
      if (!input) return null;
      // Size gate clears the value to prevent adding without size selection
      if (!input.value || input.dataset.sizeGated === 'true') return null;
      return input.value;
    }

    function getVariantPrice() {
      // Same scoping as getVariantId: an unscoped lookup reads the underlying
      // product page's price when this selector is inside the quick-add modal.
      var scope = container.closest('#quick-add-modal-content, .shopify-section') || document;
      var priceEl = scope.querySelector('[data-product-price]');
      if (priceEl && priceEl.dataset.productPrice) {
        return parseInt(priceEl.dataset.productPrice, 10);
      }
      // Fallback: use the base price from server render
      return basePrice;
    }

    function enableIfVariant() {
      var variantId = getVariantId();
      if (!variantId) {
        addBtn.disabled = true;
        return;
      }
      // Variantti on valittu, mutta se EI yksin riita napin avaamiseen:
      // `size-gate.js` voi pitaa nappia kiinni kahdesta syysta (kokoa ei ole
      // valittu, tai valittu koko on loppuunmyyty). Tama ajetaan myos
      // `variant:update`-kuuntelijasta — ennen taman korjausta se avasi napin
      // uudelleen heti kun loppuneelle koolle tuli variantti-id, eli
      // "Loppuunmyyty" oli klikattavissa (sama vikaluokka 7.10.2026).
      addBtn.disabled = isBuyBlocked();
    }

    /**
     * Onko `size-gate.js` estanyt ostamisen? Luetaan sen omistamasta tilasta
     * DOMissa — ei toista saatavuuslaskentaa tanne, jotta totuuden lahde
     * pysyy yhdessa paikassa.
     *
     *   `input[name="id"][data-size-gated]` — kokoa ei ole valittu
     *   `[data-pack-bis]` nakyvissa        — valittu koko on loppu
     *
     * Fail-open: jos size-gate ei ole (vielä) ladannut, ei estetä myyntia.
     */
    function isBuyBlocked() {
      var gateScope = container.closest('#quick-add-modal-content, .shopify-section') || document;
      var gateInput = gateScope.querySelector('product-form-component input[name="id"], [data-product-form] input[name="id"]');
      if (gateInput && gateInput.dataset.sizeGated === 'true') return true;

      var anchor = gateScope.querySelector('[data-pack-bis]');
      if (anchor && !anchor.hidden) return true;

      return false;
    }

    function updateDisplayForPrice(price) {
      basePrice = price;
      // Horizon's product-price.js calls replaceWith() on variant change,
      // so wait for the new node to settle, then re-capture original price.
      // Important: if pack pricing was active before variant change, the
      // priceContainer may still show the old pack total — clear pack state
      // first so captureOriginalPrice() reads Horizon's newly-rendered
      // single-item price, not our stale pack HTML.
      setTimeout(function() {
        var wasPackActive = packPriceActive;
        packPriceActive = false;
        captureOriginalPrice();
        updateOptionSavings();
        if (wasPackActive && selectedQuantity > 1 && selectedPct > 0) {
          updatePriceDisplay();
        }
      }, 50);
    }

    /**
     * Black Horse: fill the Pakettitaso text tokens. Mirrors the replace chain in
     * snippets/pack-selector-tile.liquid, which renders the same text on first paint.
     */
    function renderTokens(template, qty, pct, math) {
      return template
        .split('[m\u00E4\u00E4r\u00E4]').join(String(qty))
        .split('[ale]').join(pct + '%')
        .split('[s\u00E4\u00E4st\u00F6]').join(formatMoney(math.savings))
        .split('[hinta]').join(formatMoney(math.total))
        .split('[kpl-hinta]').join(formatMoney(Math.floor(math.total / qty)));
    }

    function updateOptionSavings() {
      options.forEach(function(opt) {
        var qty = parseInt(opt.dataset.packQuantity, 10) || 1;
        var pct = parseInt(opt.dataset.packPct, 10) || 0;
        var free = parseInt(opt.dataset.packFree, 10) || 0;
        var math = packMath(qty, pct, free);
        var hasSaving = pct > 0 || free > 0;

        var titleEl = opt.querySelector('[data-pack-title-out]');
        if (titleEl && opt.dataset.packTitle) {
          titleEl.textContent = renderTokens(opt.dataset.packTitle, qty, pct, math);
        }

        var savingsEl = opt.querySelector('[data-pack-sub-out]');
        if (savingsEl) {
          var sub = opt.dataset.packSub
            ? renderTokens(opt.dataset.packSub, qty, pct, math)
            : (hasSaving ? 'S\u00E4\u00E4st\u00E4 ' + formatMoney(math.savings) : '');
          savingsEl.textContent = sub;
          savingsEl.classList.toggle('pack-selector__savings--empty', !sub);
        }

        var totalEl = opt.querySelector('[data-pack-total]');
        if (totalEl) totalEl.textContent = formatMoney(math.total);
        var compareEl = opt.querySelector('[data-pack-compare]');
        if (compareEl) {
          compareEl.innerHTML = '<span class="visually-hidden">Normaalihinta </span>' + formatMoney(basePrice * qty);
        }
      });
    }

    // Cache the stable <product-price> custom element (never replaced by Horizon).
    // The inner <div ref="priceContainer"> gets replaced on variant:update,
    // so we look it up fresh on every updatePriceDisplay() call.
    var productPriceEl = null;
    var sectionEl = container.closest('#quick-add-modal-content, .shopify-section');
    if (sectionEl) {
      productPriceEl = sectionEl.querySelector('product-price');
    }
    if (!productPriceEl && !container.closest('#quick-add-modal-content')) {
      // Only fall back to a document-wide lookup outside the modal; inside it,
      // that would bind to the underlying product page's price element.
      productPriceEl = document.querySelector('product-price');
    }

    var originalPriceHTML = '';
    var packPriceActive = false;

    function getPriceContainer() {
      if (!productPriceEl) return null;
      return productPriceEl.querySelector('[ref="priceContainer"]');
    }

    function captureOriginalPrice() {
      var pc = getPriceContainer();
      if (pc) originalPriceHTML = pc.innerHTML;
    }

    // Delay initial capture so Horizon hydration has settled.
    setTimeout(captureOriginalPrice, 200);

    function updatePriceDisplay() {
      var pc = getPriceContainer();
      if (!pc) return;

      if (selectedQuantity > 1 && (selectedPct > 0 || selectedFree > 0)) {
        packPriceActive = true;
        var math = packMath(selectedQuantity, selectedPct, selectedFree);
        var packTotal = math.total;
        var compareTotal = basePrice * selectedQuantity;
        // "Alunperin:" text comes from Teema-asetukset → Hinnat (data-original-label).
        var originalLabel = container.getAttribute('data-original-label');
        if (originalLabel === null) originalLabel = 'Alunperin:';
        var originalLabelHtml = originalLabel
          ? '<span class="price-original-label">' + originalLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</span> '
          : '';
        var savingsLabel = selectedFree > 0
          ? '-' + formatMoney(math.savings)
          : '-' + selectedPct + '%';

        pc.innerHTML =
          '<span role="group">' +
            '<span class="visually-hidden">Tarjoushinta</span>' +
            '<span class="price discount-price">' + formatMoney(packTotal) + '</span>' +
            '<span class="tax-note-inline"> sis. alv</span>' +
          '</span>' +
          '<div class="price-original-line">' +
            '<span class="visually-hidden">Normaalihinta</span>' +
            originalLabelHtml + '<span class="compare-at-price">' + formatMoney(compareTotal) + '</span>' +
            ' <span class="discount-text">' + savingsLabel + '</span>' +
          '</div>';
      } else {
        packPriceActive = false;
        pc.innerHTML = originalPriceHTML;
      }
    }

    function updateSummary() {
      if (!summaryEl) return;

      if (selectedQuantity > 1 && (selectedPct > 0 || selectedFree > 0)) {
        summaryEl.innerHTML = 'M\u00E4\u00E4r\u00E4: <strong>' + selectedQuantity +
          '</strong> | <strong>' + formatMoney(packMath(selectedQuantity, selectedPct, selectedFree).unit) +
          '</strong> / kpl';
      } else {
        summaryEl.innerHTML = 'M\u00E4\u00E4r\u00E4: <strong>' + selectedQuantity +
          '</strong> | <strong>' + formatMoney(basePrice) + '</strong> / kpl';
      }

      // Update add button text — but respect size gate AND sold-out state.
      //
      // Molemmat tilat omistaa `size-gate.js`; tama lohko vain valttaa
      // ylikirjoittamasta niita. Pelkka sizeGated-tarkistus ei riittanyt:
      // kun koko oli VALITTU mutta LOPPU, pakettilaatan klikkaus kirjoitti
      // "Lisaa 5 kpl ostoskoriin" disabloidun "Loppuunmyyty"-napin paalle
      // ja nappi valehteli saatavuudesta (kayttajan loydos 7.10.2026).
      var isSizeGated = false;
      var gateScope = container.closest('#quick-add-modal-content, .shopify-section') || document;
      var variantInput = gateScope.querySelector('product-form-component input[name="id"], [data-product-form] input[name="id"]');
      if (variantInput && variantInput.dataset.sizeGated === 'true') {
        isSizeGated = true;
      }

      // Loppuunmyyty-tila luetaan DOMista: BIS-ankkuri on nakyvissa
      // tasmalleen silloin kun valittu koko on loppu. Yksi totuuden lahde
      // (size-gate.js) — ei toista saatavuuslaskentaa tanne.
      var bisAnchor = gateScope.querySelector('[data-pack-bis]');
      var isSoldOut = !!(bisAnchor && !bisAnchor.hidden);

      if (textEl && !isSizeGated && !isSoldOut) {
        if (selectedQuantity > 1) {
          textEl.textContent = 'Lis\u00E4\u00E4 ' + selectedQuantity + ' kpl ostoskoriin';
        } else {
          textEl.textContent = 'Lis\u00E4\u00E4 ostoskoriin';
        }
      }
      // Keep button disabled if size not selected or the selected size is sold out
      if (isSizeGated || isSoldOut) {
        addBtn.disabled = true;
      }

      // Update the product price display
      updatePriceDisplay();

    }

    function selectOption(option) {
      options.forEach(function(opt) {
        opt.classList.remove('pack-selector__option--selected');
        opt.setAttribute('aria-checked', 'false');
      });
      option.classList.add('pack-selector__option--selected');
      option.setAttribute('aria-checked', 'true');

      selectedQuantity = parseInt(option.dataset.packQuantity, 10) || 1;
      selectedPct = parseInt(option.dataset.packPct, 10) || 0;
      selectedFree = parseInt(option.dataset.packFree, 10) || 0;

      updateSummary();
    }

    function setState(state) {
      if (textEl) textEl.hidden = state !== 'default';
      if (loadingEl) loadingEl.hidden = state !== 'loading';
      if (successEl) successEl.hidden = state !== 'success';

      // Spinnerin ja onnistumisen aikana nappi on aina kiinni. Kun palataan
      // 'default'-tilaan, EI avata nappia ehdoitta: `size-gate.js` voi pitaa
      // sita kiinni (kokoa ei valittu / koko loppuunmyyty). Ennen tata
      // lisayksen jalkeinen palautus avasi "Loppuunmyyty"-napin uudelleen.
      if (state === 'loading' || state === 'success') {
        addBtn.disabled = true;
      } else {
        addBtn.disabled = isBuyBlocked();
      }
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
      setTimeout(function() {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }, ERROR_DURATION);
    }

    // Bind click on each pack option
    options.forEach(function(option) {
      option.addEventListener('click', function() {
        selectOption(option);
      });
    });

    // Add to cart
    addBtn.addEventListener('click', function() {
      var variantId = getVariantId();
      if (!variantId) {
        showError('Valitse ensin koko tai malli.');
        return;
      }

      setState('loading');

      // Collect section IDs for cart drawer morph
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function(el) {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: selectedQuantity }],
          sections: sectionIds.join(',')
        })
      })
        .then(function(response) {
          if (!response.ok) {
            return response.json().then(function(data) {
              throw new Error(data.description || data.message || 'Tuntematon virhe');
            });
          }
          return response.json();
        })
        .then(function(data) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: {
              resource: data,
              data: { sections: data.sections || {} }
            }
          }));
          setState('success');

          // Close the quick-add modal once the item is in. Stock quick-add
          // relies on QuickAddDialog's own cart:update handler, but that
          // listener is bound to the dialog element rather than to document,
          // so the event dispatched above never reaches it. Leaving the modal
          // open hides the cart drawer the add just opened.
          var quickAddDialog = container.closest('#quick-add-modal-content')
            ? document.getElementById('quick-add-dialog')
            : null;

          setTimeout(function() {
            setState('default');
            enableIfVariant();
            if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
              quickAddDialog.closeDialog();
            }
          }, SUCCESS_DURATION);
        })
        .catch(function(err) {
          setState('default');
          enableIfVariant();
          if (err.message && err.message !== 'Failed to fetch') {
            showError(err.message);
          } else {
            showError('Yhteysvirhe. Yrit\u00E4 uudelleen.');
          }
        });
    });

    // Initialize state
    enableIfVariant();

    // Listen for variant changes — update price displays
    document.addEventListener('variant:update', function(e) {
      if (container.dataset.packInit !== 'true') return;
      enableIfVariant();

      // DO NOT call updateSummary() here — it would write pack HTML back
      // into priceContainer before captureOriginalPrice() sees Horizon's
      // fresh single-item price. updateDisplayForPrice handles both
      // re-capture and pack re-overlay in the correct order.

      // Try to get new variant price from event detail
      var detail = e.detail || {};
      var variant = detail.variant || detail.resource || {};
      if (variant.price) {
        updateDisplayForPrice(variant.price);
      } else {
        // Fallback: re-read from DOM after a tick
        setTimeout(function() {
          var newPrice = getVariantPrice();
          if (newPrice && newPrice !== basePrice) {
            updateDisplayForPrice(newPrice);
          } else {
            // Even if price is unchanged, we still need to refresh the
            // summary text (unit price, add-button label) after variant
            // change — but by now Horizon's replaceWith has settled, so
            // it's safe to re-render pack HTML.
            updateSummary();
          }
        }, 50);
      }
    });

    // Fallback: listen for select/radio changes in product section
    var productSection = container.closest('.shopify-section');
    if (productSection) {
      productSection.addEventListener('change', function(e) {
        if (e.target.matches('select, input[type="radio"]')) {
          setTimeout(enableIfVariant, 50);
        }
      });
    }
  }

  function checkForSelectors() {
    var selectors = document.querySelectorAll('[data-pack-selector]');
    selectors.forEach(function(sel) {
      if (sel.dataset.packInit !== 'true') {
        initPackSelector(sel);
      }
    });
  }

  // Poll for pack selectors (handles lazy hydration)
  checkForSelectors();
  var pollInterval = setInterval(checkForSelectors, 500);
  setTimeout(function() {
    clearInterval(pollInterval);
  }, 30000);

  // Re-check after variant updates
  document.addEventListener('variant:update', function() {
    setTimeout(checkForSelectors, 100);
  });

  // Re-check after cart morph
  document.addEventListener('cart:update', function() {
    setTimeout(checkForSelectors, 100);
  });

  // The quick-add modal injects a whole product page long after the 30s poll
  // has expired — from stock quick-add, cart recommendations, or the sock
  // upsell. Its content element is in the static DOM from page load, so
  // observing it covers every injection path.
  function observeQuickAddModal() {
    var modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) {
      // The modal is rendered near the end of theme.liquid, so it may not
      // exist yet when this deferred script runs.
      setTimeout(observeQuickAddModal, 1000);
      return;
    }
    new MutationObserver(function() {
      setTimeout(checkForSelectors, 100);
    }).observe(modalContent, { childList: true });
  }
  observeQuickAddModal();
})();
